/**
 * The browser half of the harness carrier's unary RPC.
 *
 * The harness's served web app talks to its host over HTTP for unary RPC and a WebSocket
 * at `/api/remote.mux` for live Remote streams. The socket is now served natively by the
 * Orik Node host on the same origin, so the client's own mux opens it with no help: the
 * only thing this deployment has to translate is the RPC prefix.
 *
 * Their prefix is a module constant (`/api`) and Orik already answers `/api` for its own
 * routes, so the request is rewritten onto the Orik route that serves the harness surface
 * (`/api/harness`, which forwards to the caller's own tenant runtime) rather than patching
 * anything of theirs. `__DSH_TRANSPORT__` is the seam the harness already declares for a
 * shell that owns a different physical carrier — the same one its desktop and worker
 * previews use — and it is installed before the client plugins boot.
 */

import { describeSettings, mutateSettings, type PathOp } from "../../../harness/orik-settings";

/** Their carrier's fixed prefix, which Orik already answers for its own routes. */
const CARRIER_PREFIX = "/api";
/** The Orik route that serves the harness session surface. */
const SURFACE_PREFIX = "/api/harness";

interface DshTransport {
  fetch(input: URL | string, init: RequestInit): Promise<Response>;
  /**
   * The page owns its host: the harness then treats the host's settings surface as reachable (`isLoopback`), and
   * its settings forms read and write the settings document, which this transport answers in the page.
   */
  ownsHost: boolean;
}

type TransportGlobal = { __DSH_TRANSPORT__?: DshTransport };

/**
 * Rewrite one of their carrier URLs onto the Orik route that serves it.
 *
 * dsh 0.2 passes the RPC target as a *document-relative string* (`api/session/list`), not a `URL`:
 * reading `input.href` off that string was `new URL(undefined)`, so every unary RPC — the session list,
 * the model catalogue, history pages — failed with "Failed to construct 'URL': Invalid URL" and the
 * catalogue never became ready. Relative targets resolve against the document, as `fetch` would.
 */
export function harnessCarrierUrl(input: URL | string, base: string = documentBase()): URL {
  const url = new URL(typeof input === "string" ? input : input.href, base);
  if (url.pathname === CARRIER_PREFIX || url.pathname.startsWith(`${CARRIER_PREFIX}/`)) {
    url.pathname = `${SURFACE_PREFIX}${url.pathname.slice(CARRIER_PREFIX.length)}`;
  }
  return url;
}

/** The base a relative carrier target resolves against: the page, as `fetch` itself would use. */
function documentBase(): string {
  return typeof document !== "undefined" ? document.baseURI : (globalThis as { location?: { href?: string } }).location?.href ?? "http://localhost/";
}

/**
 * Install the carrier before the client plugins boot.
 *
 * Only the unary `fetch` is overridden. Leaving `openStream` unset is deliberate: the
 * Gateway's own WebSocket mux is then used, which is what makes the native
 * `/api/remote.mux` socket the live-stream transport instead of a second protocol.
 */
export function installHarnessTransport(): void {
  (globalThis as TransportGlobal).__DSH_TRANSPORT__ = {
    ownsHost: true,
    fetch: async (input, init) => localAnswer(harnessCarrierUrl(input), init) ?? await fetch(harnessCarrierUrl(input), init),
  };
}

/**
 * The harness settings document, answered in the page.
 *
 * The harness's settings forms (`ui-settings`) read the settings document on every (re)connection and write to it,
 * because this transport owns the host. Orik's host keeps no settings document; this browser's does
 * (`harness/orik-settings.ts`), so both requests are answered here and never reach the network.
 */
const SETTINGS_DESCRIBE = `${SURFACE_PREFIX}/settings/describe`;
const SETTINGS_MUTATE = `${SURFACE_PREFIX}/settings/mutate`;

/** The connection's unary request envelope, as far as the settings answers read it. */
interface ClientRequest {
  rpcId?: unknown;
  /** A generated Remote method's arguments, by wire name. */
  payload?: { args?: { ns?: unknown; ops?: unknown; expectedRevision?: unknown } };
}

/** Answer a carrier request in the page when this deployment's answer is the page's own; otherwise undefined. */
export function localAnswer(url: URL, init: RequestInit): Promise<Response> | undefined {
  if ((url.pathname !== SETTINGS_DESCRIBE && url.pathname !== SETTINGS_MUTATE) || typeof init.body !== "string") return undefined;
  const { rpcId, payload } = JSON.parse(init.body) as ClientRequest;
  let result: { ok: true; value: unknown } | { ok: false; error: unknown };
  if (url.pathname === SETTINGS_DESCRIBE) {
    result = { ok: true, value: describeSettings() };
  } else {
    const args = payload?.args;
    const ns = typeof args?.ns === "string" ? args.ns : "";
    const ops = Array.isArray(args?.ops) ? args.ops as PathOp[] : [];
    const expected = typeof args?.expectedRevision === "number" ? args.expectedRevision : undefined;
    result = mutateSettings(ns, ops, expected);
  }
  return Promise.resolve(Response.json({ type: "server-response", rpcId, result }));
}
